import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
} from '@mui/material'
import React, { useMemo } from 'react'
import { PaperArrastavel } from '../../dialog-arrastavel/PaperArrastavel'
import {ConsultaItens, ConsultaItensProps} from "./ConsultaItens";
import { RodapeConsultaItens } from "./RodapeConsultaItens";
import { faXmark } from "@fortawesome/free-solid-svg-icons/faXmark";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

// Migração MUI v4 -> v9: era um `makeStyles` só para dar altura fixa ao Paper do Dialog. No v9
// isso é `slotProps.paper` (o `classes={{ paper }}` do v4 apontava para o mesmo slot).
//
// `height: 700` fixo sobrava espaço vazio embaixo quando o orçamento tinha poucos itens — virou
// `maxHeight`, então o Paper encolhe até o conteúdo e só rola (DialogContent) quando passa disso.
// Largura: `maxWidth={false}` no Dialog + width aqui ocupa a horizontal toda (margem de 8px).
// `resize: horizontal` dá o punho nativo de redimensionar (só largura) no canto inferior direito;
// ele só funciona com `overflow` diferente de `visible` — daí o `hidden` (a rolagem do conteúdo
// continua no DialogContent, então não aparece barra dupla).
const ESTILO_PAPER_DIALOG = {
    // O tema do MUI (materialUiTheme.ts) não define `palette.background.paper`, então o Paper sai
    // branco fixo — inclusive no tema escuro. Amarrar no token deixa o Paper e as linhas da grade
    // (que usam o mesmo `--mu-superficie`) com a MESMA cor nos dois temas.
    backgroundColor: "var(--mu-superficie, #ffffff)",
    m: 1,
    width: "calc(100% - 16px)",
    maxWidth: "calc(100% - 16px)",
    maxHeight: "calc(100% - 16px)",
    resize: "horizontal",
    overflow: "hidden"
}

// Título em barra: texto à esquerda, botão de fechar à direita (afastado da borda).
// O `cursor: move` / `user-select` da barra vêm do padrão global (dialogArrastavel.scss), não daqui.
const ESTILO_TITULO_DIALOG = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    py: 1.5,
    borderBottom: "1px solid var(--mu-borda-media, #E4E6E8)"
}

// O conteúdo é uma coluna flex que NÃO rola: quem rola é a área da grade lá dentro
// (`.areaRolavel`), para o cabeçalho fixo da tabela ter um scrollport próprio onde grudar.
// `minHeight: 0` deixa o DialogContent encolher abaixo do conteúdo, senão o Paper estoura.
const ESTILO_CONTEUDO_DIALOG = {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "hidden"
}

// Rodapé fixo: totais + Fechar. Fica FORA do DialogContent de propósito — dentro dele rolaria
// junto com a grade e sumiria da vista quando o orçamento tem muitos itens.
const ESTILO_ACOES_DIALOG = {
    borderTop: "1px solid var(--mu-borda-media, #E4E6E8)",
    px: 3,
    py: 1,
    // O DialogActions alinha os filhos à direita; aqui o filho é a faixa inteira do rodapé.
    "& > *": { flex: 1 }
}

// Botão "fantasma" (só ícone, sem fundo): repouso em tom claro e ESCURECE no hover/active, com o
// realce neutro do padrão de botão-ícone do projeto — rgba(0,0,0,.06) no hover e .12 no active
// (ver #CSS-169 em Aprendizado_CSS.md / mixin `hoverAtivoIcone` em estilos/botoes.module.scss).
const ESTILO_BOTAO_FECHAR = {
    // Afastado da borda do Paper — encostado no canto o alvo de clique fica espremido.
    mr: 1,
    color: "var(--mu-texto-desab, #bbbbbb)",
    "&:hover": {
        color: "var(--mu-texto, #333739)",
        backgroundColor: "rgba(0, 0, 0, 0.06)"
    },
    "&:active": {
        color: "var(--mu-texto, #333739)",
        backgroundColor: "rgba(0, 0, 0, 0.12)"
    }
}

export type ConsultaItensDialogProps = {
    open: boolean
    onClose: () => void
} & ConsultaItensProps

export const ConsultaItensDialog = React.memo((props: ConsultaItensDialogProps) => {
    const memoizedConsultaItens = useMemo(() => (
        <ConsultaItens 
            dadosOrcamento={props.dadosOrcamento}
            rotuloColunaCodigoReferencia={props.rotuloColunaCodigoReferencia}
            numeroCasasDecimaisQuantidade={props.numeroCasasDecimaisQuantidade}
            numeroCasasDecimaisPreco={props.numeroCasasDecimaisPreco}
            codigoFilialOrcamento={props.codigoFilialOrcamento}
            corPorAmbiente={props.corPorAmbiente}
            colunas={props.colunas}
            tamanhoFonte={props.tamanhoFonte}
            onToggleColunaSelecionada={props.onToggleColunaSelecionada}
            onToggleSelecionarTodasColunas={props.onToggleSelecionarTodasColunas} 
            parametro_CHAVE_MOVIMENTACAO_ORCAMENTO={props.parametro_CHAVE_MOVIMENTACAO_ORCAMENTO} 
            permiteEngrenagemGrid={props.permiteEngrenagemGrid}
        />
    ), [
        props.dadosOrcamento, 
        props.rotuloColunaCodigoReferencia, 
        props.numeroCasasDecimaisQuantidade, 
        props.numeroCasasDecimaisPreco, 
        props.codigoFilialOrcamento, 
        props.corPorAmbiente, 
        props.colunas, 
        props.tamanhoFonte, 
        props.onToggleColunaSelecionada, 
        props.onToggleSelecionarTodasColunas, 
        props.parametro_CHAVE_MOVIMENTACAO_ORCAMENTO, 
        props.permiteEngrenagemGrid
    ]);

    return (
        <Dialog
            fullWidth
            id="ConsultaItens"
            maxWidth={false}
            open={props.open}
            onClose={props.onClose}
            PaperComponent={PaperArrastavel}
            slotProps={{ paper: { sx: ESTILO_PAPER_DIALOG } }}
        >
            <DialogTitle
                id="ConsultaItens_Titulo"
                sx={ESTILO_TITULO_DIALOG}
            >
                <span id="ConsultaItens_Titulo_Texto">Consulta de Itens</span>

                <IconButton
                    id="ConsultaItens_Titulo_BotaoFechar"
                    onClick={props.onClose}
                    aria-label="Fechar"
                    title="Fechar"
                    size="small"
                    sx={ESTILO_BOTAO_FECHAR}
                >
                    <FontAwesomeIcon icon={faXmark} />
                </IconButton>
            </DialogTitle>
            <DialogContent
                id="ConsultaItens_DialogContent"
                sx={ESTILO_CONTEUDO_DIALOG}
            >
                {props.dadosOrcamento != null && memoizedConsultaItens}
            </DialogContent>
            <DialogActions
                id="ConsultaItens_DialogActions"
                sx={ESTILO_ACOES_DIALOG}
            >
                {props.dadosOrcamento != null &&
                    <RodapeConsultaItens
                        dadosOrcamento={props.dadosOrcamento}
                        numeroCasasDecimaisPreco={props.numeroCasasDecimaisPreco}
                        onFechar={props.onClose}
                    />
                }
            </DialogActions>
        </Dialog>
    )
});
